/**
 * Know Your Setting - Dynamic Variant Updates
 * Updates setting specifications based on variant selection
 */

class KnowYourSetting {
  constructor() {
    this.container = document.querySelector('.know-your-setting');
    this.variantData = window.knowYourSettingData || {};
    this.init();
  }

  init() {
    if (!this.container) return;
    
    // Listen for variant changes
    this.bindEvents();
    
    // Initialize with current variant
    this.updateFromCurrentVariant();
  }

  bindEvents() {
    // Listen for variant change events from the theme
    document.addEventListener('variantChange', (event) => {
      this.updateFromVariant(event.detail.variant);
    });

    // Listen for variant selector changes
    const variantSelectors = document.querySelectorAll('.single-option-selector, .product-form__variants');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        setTimeout(() => {
          this.updateFromCurrentVariant();
        }, 100);
      });
    });

    // Listen for radio button changes
    const radioButtons = document.querySelectorAll('input[type="radio"][name*="option"]');
    radioButtons.forEach(radio => {
      radio.addEventListener('change', () => {
        setTimeout(() => {
          this.updateFromCurrentVariant();
        }, 100);
      });
    });

    // Listen for swatch/color changes
    const swatchButtons = document.querySelectorAll('.single-option-radio label, .swatch-element');
    swatchButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => {
          this.updateFromCurrentVariant();
        }, 200);
      });
    });

    // Listen for any form changes in the product form
    const productForm = document.querySelector('#AddToCartForm, .product-form');
    if (productForm) {
      productForm.addEventListener('change', () => {
        setTimeout(() => {
          this.updateFromCurrentVariant();
        }, 100);
      });
    }

    // Use MutationObserver to detect DOM changes
    const observer = new MutationObserver(() => {
      this.updateFromCurrentVariant();
    });

    // Observe changes to the product form
    if (productForm) {
      observer.observe(productForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-selected']
      });
    }
  }

  updateFromCurrentVariant() {
    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.updateFromVariant(currentVariant);
    }
  }

  getCurrentVariant() {
    // Try to get variant from theme's variant system
    if (window.theme && window.theme.variants && window.theme.variants.currentVariant) {
      return window.theme.variants.currentVariant;
    }

    // Try to get from slate.Variants if available
    if (window.slate && window.slate.Variants) {
      const variantInstance = document.querySelector('.product-single');
      if (variantInstance && variantInstance.variants && variantInstance.variants.currentVariant) {
        return variantInstance.variants.currentVariant;
      }
    }

    // Try to get from product form
    const variantSelect = document.querySelector('#ProductSelect, .product-form__variants, select[name="id"]');
    if (variantSelect && variantSelect.value) {
      const variantId = variantSelect.value;
      return this.variantData.variants && this.variantData.variants[variantId];
    }

    // Try to get from selected options
    const selectedOptions = this.getSelectedOptions();
    if (selectedOptions.length > 0) {
      return this.findVariantByOptions(selectedOptions);
    }

    // Last resort: try to get from URL or first available variant
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId && this.variantData.variants && this.variantData.variants[variantId]) {
      return this.variantData.variants[variantId];
    }

    return null;
  }

  getSelectedOptions() {
    const options = [];
    
    // Check select dropdowns
    const optionSelectors = document.querySelectorAll('.single-option-selector, select[name*="option"]');
    optionSelectors.forEach(selector => {
      if (selector.value && selector.value !== '') {
        options.push(selector.value);
      }
    });

    // Check radio buttons
    const radioButtons = document.querySelectorAll('input[type="radio"][name*="option"]:checked');
    radioButtons.forEach(radio => {
      if (radio.value) {
        options.push(radio.value);
      }
    });

    // Check for swatch selections
    const swatchSelected = document.querySelectorAll('.single-option-radio input:checked');
    swatchSelected.forEach(swatch => {
      if (swatch.value) {
        options.push(swatch.value);
      }
    });

    // If no options found, try to get from display elements
    if (options.length === 0) {
      const displayElements = document.querySelectorAll('.js-swatch-display--1, .js-swatch-display--2, .js-swatch-display--3');
      displayElements.forEach(element => {
        if (element.textContent && element.textContent.trim() !== '') {
          options.push(element.textContent.trim());
        }
      });
    }

    return options;
  }

  findVariantByOptions(selectedOptions) {
    if (!this.variantData.variants) return null;

    for (const [variantId, variant] of Object.entries(this.variantData.variants)) {
      const variantParts = variant.title.split(' / ');
      if (this.arraysEqual(selectedOptions, variantParts)) {
        return { id: variantId, ...variant };
      }
    }

    return null;
  }

  arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, index) => val === arr2[index]);
  }

  updateFromVariant(variant) {
    if (!variant || !this.container) return;

    this.showLoadingState();

    // Update variant data attributes
    this.container.setAttribute('data-variant-id', variant.id);
    this.container.setAttribute('data-metal', variant.metal || '');
    this.container.setAttribute('data-diamond', variant.diamond || '');
    this.container.setAttribute('data-carat', variant.carat || '');

    // Update metal information
    this.updateMetalInfo(variant);

    // Update TCW based on carat
    this.updateTCW(variant);

    // Update accent gems description
    this.updateAccentGems(variant);

    // Update center stone information
    this.updateCenterStone(variant);

    // Hide loading state
    setTimeout(() => {
      this.hideLoadingState();
    }, 300);
  }

  updateMetalInfo(variant) {
    // Update metal title in the metal section
    const metalTitle = this.container.querySelector('.metal-title');
    if (metalTitle && variant.metal) {
      metalTitle.textContent = variant.metal;
    }

    // Update metal composition
    const composition = this.getMetalComposition(variant.metal);
    if (composition) {
      this.updateMetalComposition(this.container, composition);
    }
  }

  getMetalComposition(metalType) {
    const compositions = {
      '14K Yellow Gold': { gold: 58.5, copper: 30.5, silver: 6.1, zinc: 4.7 },
      '14K White Gold': { gold: 58.5, palladium: 25, silver: 10, zinc: 6.5 },
      '14K Rose Gold': { gold: 58.5, copper: 35, silver: 6.5 },
      '18K Yellow Gold': { gold: 75, copper: 21.8, silver: 2.2, zinc: 0.8 },
      '18K White Gold': { gold: 75, copper: 21.8, silver: 2.2, zinc: 0.8 },
      '18K Rose Gold': { gold: 75, copper: 21.8, silver: 2.2, zinc: 0.8 },
      '10K Yellow Gold': { gold: 41.7, copper: 40, silver: 18.3 },
      '10K White Gold': { gold: 41.7, palladium: 30, silver: 28.3 },
      '10K Rose Gold': { gold: 41.7, copper: 45, silver: 13.3 },
      'Sterling Silver': {silver: 92.5, others: 7.5 },
    };

    return compositions[metalType] || null;
  }

  updateMetalComposition(container, composition) {
    // Update the SVG graph
    const svg = container.querySelector('.composition-svg');
    if (svg) {
      // Clear existing circles
      const existingCircles = svg.querySelectorAll('.composition-segment');
      existingCircles.forEach(circle => circle.remove());
      
      // Add new circles for each component
      const circumference = 251.33; // 2 * π * 40
      let offset = 0;
      
      Object.entries(composition).forEach(([name, percentage]) => {
        const dashLength = (percentage / 100) * circumference;
        const colorClass = name.toLowerCase().replace(' ', '-');
        
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', '50');
        circle.setAttribute('cy', '50');
        circle.setAttribute('r', '40');
        circle.setAttribute('fill', 'none');
        circle.setAttribute('stroke', `url(#gradient-${colorClass})`);
        circle.setAttribute('stroke-width', '18');
        circle.setAttribute('stroke-dasharray', `${dashLength} ${circumference}`);
        circle.setAttribute('stroke-dashoffset', `${offset}`);
        circle.setAttribute('transform', 'rotate(-90 50 50)');
        circle.setAttribute('class', `composition-segment composition-${colorClass}`);
        
        svg.appendChild(circle);
        offset -= dashLength;
      });
    }

    // Update the composition details
    const compositionGrid = container.querySelector('.composition-grid');
    if (compositionGrid) {
      const detailsHTML = Object.entries(composition)
        .map(([name, percentage]) => `
          <div class="composition-item">
            <div class="composition-dot composition-${name.toLowerCase().replace(' ', '-')}"></div>
            <span class="composition-percentage">${percentage}%</span>
            <span class="composition-name">${name.charAt(0).toUpperCase() + name.slice(1)}</span>
          </div>
        `).join('');
      
      compositionGrid.innerHTML = detailsHTML;
    }
  }

  updateTCW(variant) {
    // Update side stone carat (TCW) in the side stone section
    const sideCaratButton = this.container.querySelector('[data-spec="side-carat"]');
    if (sideCaratButton && variant.carat) {
      const valueSpan = sideCaratButton.querySelector('.button-value');
      if (valueSpan) {
        // Calculate TCW based on carat
        const caratNumber = parseFloat(variant.carat.replace(/[^\d.]/g, ''));
        if (!isNaN(caratNumber)) {
          const settingWeight = 0.03;
          const calculatedTCW = (caratNumber + settingWeight).toFixed(2);
          valueSpan.textContent = calculatedTCW;
        }
      }
    }
  }

  updateAccentGems(variant) {
    // Update side stone color and clarity based on diamond type
    const sideColorInfo = this.container.querySelector('[data-spec="side-color"]');
    const sideClarityInfo = this.container.querySelector('[data-spec="side-clarity"]');

    if (variant.diamond) {
      if (variant.diamond.includes('Moissanite')) {
        if (sideColorInfo) {
          const valueSpan = sideColorInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'D';
        }
        if (sideClarityInfo) {
          const valueSpan = sideClarityInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'VVS1';
        }
      } else if (variant.diamond.includes('Lab Grown')) {
        if (sideColorInfo) {
          const valueSpan = sideColorInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'E-F';
        }
        if (sideClarityInfo) {
          const valueSpan = sideClarityInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'VVS2';
        }
      }
    }
  }

  updateCenterStone(variant) {
    // Update main stone carat
    const caratInfo = this.container.querySelector('[data-spec="carat"]');
    if (caratInfo && variant.carat) {
      const valueSpan = caratInfo.querySelector('.info-value');
      if (valueSpan) valueSpan.textContent = variant.carat;
    }

    // Update main stone color and clarity based on diamond type (same logic as side stone)
    const colorInfo = this.container.querySelector('[data-spec="color"]');
    const clarityInfo = this.container.querySelector('[data-spec="clarity"]');

    if (variant.diamond) {
      if (variant.diamond.includes('Moissanite')) {
        if (colorInfo) {
          const valueSpan = colorInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'D';
        }
        if (clarityInfo) {
          const valueSpan = clarityInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'VVS1';
        }
      } else if (variant.diamond.includes('Lab Grown')) {
        if (colorInfo) {
          const valueSpan = colorInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'E-F';
        }
        if (clarityInfo) {
          const valueSpan = clarityInfo.querySelector('.info-value');
          if (valueSpan) valueSpan.textContent = 'VVS2';
        }
      }
    } else {
      // Fallback to variant values if diamond type not available
      if (colorInfo && variant.color) {
        const valueSpan = colorInfo.querySelector('.info-value');
        if (valueSpan) valueSpan.textContent = variant.color;
      }
      if (clarityInfo && variant.clarity) {
        const valueSpan = clarityInfo.querySelector('.info-value');
        if (valueSpan) valueSpan.textContent = variant.clarity;
      }
    }

    // Update side stone carat (TCW)
    const sideCaratInfo = this.container.querySelector('[data-spec="side-carat"]');
    if (sideCaratInfo && variant.tcw) {
      const valueSpan = sideCaratInfo.querySelector('.info-value');
      if (valueSpan) valueSpan.textContent = variant.tcw;
    }
  }

  showLoadingState() {
    this.container.classList.add('updating');
  }

  hideLoadingState() {
    this.container.classList.remove('updating');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new KnowYourSetting();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new KnowYourSetting();
  });
} else {
  new KnowYourSetting();
}

// Export for potential external use
window.KnowYourSetting = KnowYourSetting;
